// frontend/src/components/Dashboard/ShipmentDetailsModal.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faTruck,
  faMapMarkerAlt,
  faCalendar,
  faBox,
  faEdit,
  faCheckCircle,
  faClock,
  faExclamationCircle,
  faShip,
  faPlane,
  faRoad,
  faUser,
  faEnvelope,
  faPhone,
  faBoxes,
  faWeightScale,
  faAnchor,
  faClipboard,
  faInfoCircle,
  faBuilding,
  faGlobe,
  faWarehouse,
  faCity,
  faSpinner,
  faSave,
  faUndo,
  faEye,
  faGlobe as faGlobeIcon,
  faMoneyBillWave,
  faLocationDot,
  faCheckDouble,
  faRocket,
  faClock as faClockIcon,
  faExclamationTriangle,
  faShieldAlt,
  faUserCheck,
  faStore,
  faTractor,
  faCrown,
  faUsers,
  faLeaf,
  faCow,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

const ShipmentDetailsModal = ({
  shipment,
  onClose,
  onUpdate,
  updateData,
  setUpdateData,
  isAdmin,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    customer: true,
    route: true,
    details: true,
    bulk: false,
    products: true,
    timeline: true,
    customs: false,
    address: true,
    dates: true,
    notes: true,
    supplier: true,
  });

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localUpdateData, setLocalUpdateData] = useState({
    status: shipment?.status || "",
    location: shipment?.currentLocation || "",
    description: "",
  });

  if (!shipment) return null;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      ordered: "#D9A321",
      processing: "#3b82f6",
      packed: "#8b5cf6",
      shipped: "#8b5cf6",
      in_transit: "#f59e0b",
      customs: "#ef4444",
      local_delivery: "#10b981",
      out_for_delivery: "#f59e0b",
      delivered: "#16a34a",
      delayed: "#dc2626",
      cancelled: "#6b7280",
    };
    return colors[status] || "#5A5A5A";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return faCheckDouble;
      case "cancelled":
        return faTimes;
      case "delayed":
        return faExclamationTriangle;
      case "shipped":
        return faRocket;
      case "in_transit":
        return faTruck;
      case "customs":
        return faShip;
      default:
        return faClockIcon;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      ordered: "📋 Order Received",
      processing: "⚙️ Processing",
      packed: "📦 Packed",
      shipped: "🚢 Shipped",
      in_transit: "🚚 In Transit",
      customs: "🛃 Customs Clearance",
      local_delivery: "🚛 Local Delivery",
      out_for_delivery: "📬 Out for Delivery",
      delivered: "✅ Delivered",
      delayed: "⚠️ Delayed",
      cancelled: "❌ Cancelled",
    };
    return labels[status] || status?.replace(/_/g, " ") || "N/A";
  };

  const getShippingMethodIcon = (method) => {
    switch (method) {
      case "air":
        return faPlane;
      case "sea":
        return faShip;
      case "road":
        return faRoad;
      case "courier":
        return faBox;
      case "combined":
        return faTruck;
      default:
        return faTruck;
    }
  };

  const getShippingMethodLabel = (method) => {
    const labels = {
      air: "✈️ Air Freight",
      sea: "🚢 Sea Freight",
      road: "🚛 Road Transport",
      courier: "📦 Courier",
      combined: "🚚 Combined",
    };
    return labels[method] || method || "N/A";
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    if (!localUpdateData.status) {
      alert("Please select a status");
      return;
    }
    setLoading(true);
    try {
      await onUpdate(
        shipment._id,
        localUpdateData.status,
        localUpdateData.location,
        localUpdateData.description,
      );
      setEditing(false);
      setLocalUpdateData({
        status: localUpdateData.status,
        location: localUpdateData.location,
        description: "",
      });
    } catch (error) {
      console.error("Error updating shipment:", error);
      alert(error.response?.data?.message || "Failed to update shipment");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setLocalUpdateData({
      status: shipment.status || "",
      location: shipment.currentLocation || "",
      description: "",
    });
  };

  const getProgressPercentage = (status) => {
    const steps = {
      ordered: 5,
      processing: 20,
      packed: 35,
      shipped: 50,
      in_transit: 65,
      customs: 75,
      local_delivery: 85,
      out_for_delivery: 95,
      delivered: 100,
      delayed: 50,
      cancelled: 0,
    };
    return steps[status] || 0;
  };

  const isDelayed = shipment.isDelayed?.() || false;
  const isFromMalaysia =
    shipment.origin?.country === SUPPLIER.country ||
    shipment.supplier?.country === SUPPLIER.country ||
    shipment.origin?.country === undefined;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content shipment-details-modal large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faTruck} /> Shipment Details
            {isFromMalaysia && (
              <span className="origin-badge">
                <FontAwesomeIcon icon={faGlobeIcon} /> {SUPPLIER.country} Origin
              </span>
            )}
            <span className="supplier-badge">
              <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
            </span>
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          {/* Tracking Header */}
          <div className="tracking-header">
            <div className="tracking-number">
              <span className="tracking-label">Tracking Number:</span>
              <span className="tracking-code">{shipment.trackingNumber}</span>
              {isFromMalaysia && (
                <span className="malaysia-tag">
                  <FontAwesomeIcon icon={faGlobeIcon} /> MY
                </span>
              )}
            </div>
            <div className="tracking-status-badges">
              {shipment.orderType === "bulk_farmer" && (
                <span className="badge bulk">🚢 Bulk Shipment</span>
              )}
              {shipment.orderType === "inventory" && (
                <span className="badge inventory">📦 Inventory</span>
              )}
              {isDelayed && <span className="badge delayed">⚠️ Delayed</span>}
              {shipment.stockUpdated && (
                <span className="badge stock-updated">✅ Stock Updated</span>
              )}
              {isFromMalaysia && (
                <span className="badge malaysia">
                  <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                </span>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-section">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${getProgressPercentage(shipment.status)}%`,
                  background: `linear-gradient(90deg, ${getStatusColor(shipment.status)}, ${getStatusColor(shipment.status)}CC)`,
                }}
              />
            </div>
            <div className="progress-labels">
              <span>Ordered</span>
              <span>Processing</span>
              <span>Shipped</span>
              <span>In Transit</span>
              <span>Customs</span>
              <span>Delivered</span>
            </div>
          </div>

          {/* Status Section */}
          <div
            className="status-section"
            style={{
              borderLeft: `4px solid ${getStatusColor(shipment.status)}`,
              background: `${getStatusColor(shipment.status)}10`,
            }}
          >
            <div className="status-header">
              <div className="status-display">
                <FontAwesomeIcon
                  icon={getStatusIcon(shipment.status)}
                  style={{ color: getStatusColor(shipment.status) }}
                />
                <span
                  className="status-label"
                  style={{ color: getStatusColor(shipment.status) }}
                >
                  {getStatusLabel(shipment.status)}
                </span>
                {shipment.currentLocation && (
                  <span className="current-location">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {shipment.currentLocation}
                  </span>
                )}
                {shipment.estimatedDelivery && (
                  <span className="estimated-delivery">
                    <FontAwesomeIcon icon={faCalendar} />
                    Est: {formatDateShort(shipment.estimatedDelivery)}
                  </span>
                )}
                {shipment.bulkDetails?.vesselName && (
                  <span className="vessel-name">
                    <FontAwesomeIcon icon={faShip} />
                    {shipment.bulkDetails.vesselName}
                  </span>
                )}
              </div>
              {isAdmin && (
                <div className="status-actions">
                  {!editing ? (
                    <button
                      className="btn-edit"
                      onClick={() => setEditing(true)}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Update Status
                    </button>
                  ) : (
                    <button className="btn-cancel" onClick={handleCancelEdit}>
                      <FontAwesomeIcon icon={faUndo} /> Cancel
                    </button>
                  )}
                </div>
              )}
            </div>

            {isAdmin && editing && (
              <form
                onSubmit={handleSubmitUpdate}
                className="status-update-form"
              >
                <div className="form-row">
                  <div className="form-group">
                    <label>Status *</label>
                    <select
                      value={localUpdateData.status}
                      onChange={(e) =>
                        setLocalUpdateData({
                          ...localUpdateData,
                          status: e.target.value,
                        })
                      }
                      className="status-select"
                      required
                    >
                      <option value="ordered">📋 Order Received</option>
                      <option value="processing">⚙️ Processing</option>
                      <option value="packed">📦 Packed</option>
                      <option value="shipped">🚢 Shipped</option>
                      <option value="in_transit">🚚 In Transit</option>
                      <option value="customs">🛃 Customs Clearance</option>
                      <option value="local_delivery">🚛 Local Delivery</option>
                      <option value="out_for_delivery">
                        📬 Out for Delivery
                      </option>
                      <option value="delivered">✅ Delivered</option>
                      <option value="delayed">⚠️ Delayed</option>
                      <option value="cancelled">❌ Cancelled</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      placeholder="Current location"
                      value={localUpdateData.location}
                      onChange={(e) =>
                        setLocalUpdateData({
                          ...localUpdateData,
                          location: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Update Description</label>
                  <input
                    type="text"
                    placeholder="Brief update description"
                    value={localUpdateData.description}
                    onChange={(e) =>
                      setLocalUpdateData({
                        ...localUpdateData,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Updating...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} /> Update Status
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Supplier Information */}
          <div className="detail-section supplier-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("supplier")}
            >
              <h3>
                <FontAwesomeIcon icon={faBuilding} /> Supplier Information
                <span className="supplier-badge">
                  <FontAwesomeIcon icon={faGlobeIcon} />{" "}
                  {shipment.supplier?.country || SUPPLIER.country}
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.supplier ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.supplier && (
              <div className="supplier-details">
                <div className="supplier-info-card">
                  <FontAwesomeIcon icon={faShip} className="supplier-icon" />
                  <div className="supplier-info">
                    <strong>{shipment.supplier?.name || SUPPLIER.name}</strong>
                    <span>
                      <FontAwesomeIcon icon={faGlobe} />{" "}
                      {shipment.supplier?.country || SUPPLIER.country} &bull;
                      <FontAwesomeIcon icon={faAnchor} />{" "}
                      {shipment.supplier?.portLoading || SUPPLIER.portLoading}
                    </span>
                  </div>
                </div>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">
                      {shipment.supplier?.location || SUPPLIER.city}
                    </span>
                  </div>
                  {shipment.origin?.warehouse && (
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faWarehouse} /> Warehouse:
                      </span>
                      <span className="detail-value">
                        {shipment.origin.warehouse}
                      </span>
                    </div>
                  )}
                  {shipment.bulkDetails?.portOfLoading && (
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faAnchor} /> Port of Loading:
                      </span>
                      <span className="detail-value">
                        {shipment.bulkDetails.portOfLoading}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Customer Information */}
          {shipment.customer?.name && (
            <div className="detail-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("customer")}
              >
                <h3>
                  <FontAwesomeIcon icon={faUser} /> Customer Information
                </h3>
                <span className="toggle-icon">
                  {expandedSections.customer ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.customer && (
                <div className="customer-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Name:</span>
                      <span className="detail-value">
                        {shipment.customer.name}
                      </span>
                    </div>
                    {shipment.customer.email && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faEnvelope} /> Email:
                        </span>
                        <span className="detail-value">
                          {shipment.customer.email}
                        </span>
                      </div>
                    )}
                    {shipment.customer.phone && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faPhone} /> Phone:
                        </span>
                        <span className="detail-value">
                          <a
                            href={`tel:${shipment.customer.phone}`}
                            className="phone-link"
                          >
                            {shipment.customer.phone}
                          </a>
                        </span>
                      </div>
                    )}
                    {shipment.customer.whatsapp && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp:
                        </span>
                        <span className="detail-value">
                          <a
                            href={`https://wa.me/${shipment.customer.whatsapp}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whatsapp-link"
                          >
                            <FontAwesomeIcon icon={faWhatsapp} />{" "}
                            {shipment.customer.whatsapp}
                          </a>
                        </span>
                      </div>
                    )}
                    {shipment.customer.userType && (
                      <div className="detail-item">
                        <span className="detail-label">Type:</span>
                        <span
                          className={`user-type-badge ${shipment.customer.userType}`}
                        >
                          {shipment.customer.userType === "farmer"
                            ? "🌾 Farmer"
                            : "🛒 Customer"}
                        </span>
                      </div>
                    )}
                    {shipment.customer.farmName && (
                      <div className="detail-item">
                        <span className="detail-label">Farm:</span>
                        <span className="detail-value">
                          {shipment.customer.farmName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Route Information */}
          <div className="detail-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("route")}
            >
              <h3>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Route Information
                <span className="route-badge">
                  <FontAwesomeIcon icon={faShip} /> {SUPPLIER.portLoading} →{" "}
                  {SUPPLIER.portDischarge}
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.route ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.route && (
              <div className="route-info">
                <div className="route-points">
                  <div className="route-point origin">
                    <div className="route-label">
                      <FontAwesomeIcon icon={faGlobeIcon} /> Origin
                    </div>
                    <div className="route-value">
                      {shipment.origin?.city ||
                        shipment.supplier?.location ||
                        SUPPLIER.city}
                    </div>
                    <div className="route-sub">
                      {shipment.origin?.country ||
                        shipment.supplier?.country ||
                        SUPPLIER.country}
                      <span className="origin-flag">🇲🇾</span>
                    </div>
                    {shipment.origin?.warehouse && (
                      <div className="route-sub">
                        <FontAwesomeIcon icon={faWarehouse} />{" "}
                        {shipment.origin.warehouse}
                      </div>
                    )}
                    {shipment.bulkDetails?.portOfLoading && (
                      <div className="route-sub">
                        <FontAwesomeIcon icon={faAnchor} /> Port:{" "}
                        {shipment.bulkDetails.portOfLoading}
                      </div>
                    )}
                  </div>
                  <div className="route-arrow">→</div>
                  <div className="route-point destination">
                    <div className="route-label">
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> Destination
                    </div>
                    <div className="route-value">
                      {shipment.destination?.city || "Freetown"}
                    </div>
                    <div className="route-sub">
                      {shipment.destination?.country || "Sierra Leone"}
                      <span className="origin-flag">🇸🇱</span>
                    </div>
                    {shipment.destination?.district && (
                      <div className="route-sub">
                        <FontAwesomeIcon icon={faCity} />{" "}
                        {shipment.destination.district}
                      </div>
                    )}
                    {shipment.bulkDetails?.portOfDischarge && (
                      <div className="route-sub">
                        <FontAwesomeIcon icon={faAnchor} /> Port:{" "}
                        {shipment.bulkDetails.portOfDischarge}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Shipment Details */}
          <div className="detail-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("details")}
            >
              <h3>
                <FontAwesomeIcon icon={faBox} /> Shipment Details
              </h3>
              <span className="toggle-icon">
                {expandedSections.details ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.details && (
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Shipping Method:</span>
                  <span className="detail-value">
                    <FontAwesomeIcon
                      icon={getShippingMethodIcon(shipment.shippingMethod)}
                    />{" "}
                    {getShippingMethodLabel(shipment.shippingMethod)}
                  </span>
                </div>
                {shipment.totalWeight > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faWeightScale} /> Total Weight:
                    </span>
                    <span className="detail-value">
                      {shipment.totalWeight} kg
                    </span>
                  </div>
                )}
                {shipment.totalQuantity > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faBoxes} /> Total Quantity:
                    </span>
                    <span className="detail-value">
                      {shipment.totalQuantity} units
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Days in Transit:</span>
                  <span className="detail-value">
                    {shipment.daysInTransit || 0} days
                  </span>
                </div>
                {shipment.order && (
                  <div className="detail-item">
                    <span className="detail-label">Order:</span>
                    <span className="detail-value">
                      #{shipment.order?.orderNumber || "N/A"}
                    </span>
                  </div>
                )}
                {shipment.trackingUrl && (
                  <div className="detail-item">
                    <span className="detail-label">Tracking URL:</span>
                    <span className="detail-value">
                      <a
                        href={shipment.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon icon={faEye} /> View Tracking
                      </a>
                    </span>
                  </div>
                )}
                {isFromMalaysia && (
                  <div className="detail-item">
                    <span className="detail-label">Origin:</span>
                    <span className="detail-value">
                      <span className="origin-tag">
                        <FontAwesomeIcon icon={faGlobeIcon} />{" "}
                        {SUPPLIER.country}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bulk Details */}
          {shipment.bulkDetails?.isBulkShipment && (
            <div className="detail-section bulk-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("bulk")}
              >
                <h3>
                  <FontAwesomeIcon icon={faShip} /> Bulk Shipment Details
                  <span className="bulk-badge">
                    <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
                  </span>
                </h3>
                <span className="toggle-icon">
                  {expandedSections.bulk ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.bulk && (
                <div className="bulk-details">
                  <div className="detail-grid">
                    {shipment.bulkDetails.containerNumber && (
                      <div className="detail-item">
                        <span className="detail-label">Container:</span>
                        <span className="detail-value">
                          {shipment.bulkDetails.containerNumber}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.containerType && (
                      <div className="detail-item">
                        <span className="detail-label">Container Type:</span>
                        <span className="detail-value">
                          {shipment.bulkDetails.containerType}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.vesselName && (
                      <div className="detail-item">
                        <span className="detail-label">Vessel:</span>
                        <span className="detail-value">
                          <FontAwesomeIcon icon={faShip} />{" "}
                          {shipment.bulkDetails.vesselName}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.voyageNumber && (
                      <div className="detail-item">
                        <span className="detail-label">Voyage:</span>
                        <span className="detail-value">
                          {shipment.bulkDetails.voyageNumber}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.portOfLoading && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faAnchor} /> Port of Loading:
                        </span>
                        <span className="detail-value">
                          {shipment.bulkDetails.portOfLoading}
                          {shipment.bulkDetails.portOfLoading ===
                            SUPPLIER.portLoading && (
                            <span className="default-tag">Default</span>
                          )}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.portOfDischarge && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faAnchor} /> Port of Discharge:
                        </span>
                        <span className="detail-value">
                          {shipment.bulkDetails.portOfDischarge}
                          {shipment.bulkDetails.portOfDischarge ===
                            SUPPLIER.portDischarge && (
                            <span className="default-tag">Default</span>
                          )}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.blNumber && (
                      <div className="detail-item">
                        <span className="detail-label">B/L Number:</span>
                        <span className="detail-value">
                          {shipment.bulkDetails.blNumber}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.sealNumber && (
                      <div className="detail-item">
                        <span className="detail-label">Seal Number:</span>
                        <span className="detail-value">
                          {shipment.bulkDetails.sealNumber}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.palletsCount > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faBoxes} /> Pallets:
                        </span>
                        <span className="detail-value">
                          {shipment.bulkDetails.palletsCount}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.weightInKg > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faWeightScale} /> Weight:
                        </span>
                        <span className="detail-value">
                          {shipment.bulkDetails.weightInKg} kg
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Products */}
          {shipment.products?.length > 0 && (
            <div className="detail-section products-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("products")}
              >
                <h3>
                  <FontAwesomeIcon icon={faBox} /> Products in Shipment
                  <span className="product-count">
                    ({shipment.products.length} items)
                  </span>
                  <span className="origin-summary">
                    <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} Origin
                  </span>
                </h3>
                <span className="toggle-icon">
                  {expandedSections.products ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.products && (
                <div className="table-responsive">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Unit</th>
                        <th>Price</th>
                        <th>Origin</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipment.products.map((product, index) => (
                        <tr key={index}>
                          <td>
                            {product.name || product.productId?.name || "N/A"}
                            {product.origin === SUPPLIER.country && (
                              <span className="origin-tag">🇲🇾</span>
                            )}
                          </td>
                          <td>{product.quantity}</td>
                          <td>{product.unit || "kg"}</td>
                          <td>${(product.price || 0).toFixed(2)}</td>
                          <td>
                            <span className="origin-tag">
                              <FontAwesomeIcon icon={faGlobe} />{" "}
                              {product.origin || SUPPLIER.country}
                            </span>
                          </td>
                          <td>
                            $
                            {((product.price || 0) * product.quantity).toFixed(
                              2,
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4"></td>
                        <td>
                          <strong>Total:</strong>
                        </td>
                        <td>
                          <strong>
                            $
                            {shipment.products
                              .reduce(
                                (sum, p) => sum + (p.price || 0) * p.quantity,
                                0,
                              )
                              .toFixed(2)}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="4"></td>
                        <td>
                          <strong>Total Weight:</strong>
                        </td>
                        <td>
                          <strong>
                            <FontAwesomeIcon icon={faWeightScale} />{" "}
                            {shipment.totalWeight ||
                              shipment.products.reduce(
                                (sum, p) => sum + (p.quantity || 0),
                                0,
                              )}{" "}
                            kg
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="4"></td>
                        <td>
                          <strong>Supplier:</strong>
                        </td>
                        <td>
                          <strong>
                            <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="detail-section timeline-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("timeline")}
            >
              <h3>
                <FontAwesomeIcon icon={faCalendar} /> Tracking Timeline
                <span className="timeline-count">
                  ({shipment.timeline?.length || 1} events)
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.timeline ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.timeline && (
              <div className="timeline">
                {shipment.timeline?.length > 0 ? (
                  shipment.timeline.map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div
                        className="timeline-dot"
                        style={{
                          background: getStatusColor(event.status),
                          borderColor:
                            index === shipment.timeline.length - 1
                              ? "#D9A321"
                              : getStatusColor(event.status),
                        }}
                      />
                      <div className="timeline-content">
                        <div className="timeline-status">
                          {getStatusLabel(event.status)}
                          {event.updatedBy && event.updatedBy !== "system" && (
                            <span className="updated-by">
                              by {event.updatedBy}
                            </span>
                          )}
                        </div>
                        {event.location && (
                          <div className="timeline-location">
                            <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                            {event.location}
                          </div>
                        )}
                        {event.description && (
                          <div className="timeline-description">
                            {event.description}
                          </div>
                        )}
                        <div className="timeline-date">
                          {formatDate(event.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="timeline-item">
                    <div
                      className="timeline-dot"
                      style={{ background: "#D9A321" }}
                    />
                    <div className="timeline-content">
                      <div className="timeline-status">Shipment Created</div>
                      <div className="timeline-date">
                        {formatDate(shipment.createdAt)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Customs Information */}
          {shipment.customs && (
            <div className="detail-section customs-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("customs")}
              >
                <h3>
                  <FontAwesomeIcon icon={faClipboard} /> Customs Information
                  <span className="customs-badge">
                    <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portDischarge}
                  </span>
                </h3>
                <span className="toggle-icon">
                  {expandedSections.customs ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.customs && (
                <div className="customs-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Cleared:</span>
                      <span
                        className={
                          shipment.customs.cleared
                            ? "status-active"
                            : "status-inactive"
                        }
                      >
                        {shipment.customs.cleared ? "✅ Cleared" : "⏳ Pending"}
                      </span>
                    </div>
                    {shipment.customs.cleared &&
                      shipment.customs.clearanceDate && (
                        <div className="detail-item">
                          <span className="detail-label">Clearance Date:</span>
                          <span className="detail-value">
                            {formatDate(shipment.customs.clearanceDate)}
                          </span>
                        </div>
                      )}
                    {shipment.customs.customsBroker && (
                      <div className="detail-item">
                        <span className="detail-label">Broker:</span>
                        <span className="detail-value">
                          {shipment.customs.customsBroker}
                        </span>
                      </div>
                    )}
                    {shipment.customs.dutyAmount > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Duty Amount:</span>
                        <span className="detail-value">
                          ${shipment.customs.dutyAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Duties Paid:</span>
                      <span className="detail-value">
                        {shipment.customs.dutiesPaid ? "✅ Yes" : "❌ No"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Port of Entry:</span>
                      <span className="detail-value">
                        <FontAwesomeIcon icon={faAnchor} />{" "}
                        {shipment.bulkDetails?.portOfDischarge ||
                          SUPPLIER.portDischarge}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Shipping Address */}
          {shipment.shippingAddress?.street && (
            <div className="detail-section address-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("address")}
              >
                <h3>
                  <FontAwesomeIcon icon={faMapMarkerAlt} /> Shipping Address
                </h3>
                <span className="toggle-icon">
                  {expandedSections.address ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.address && (
                <div className="address-details">
                  <div className="detail-grid">
                    {shipment.shippingAddress.recipientName && (
                      <div className="detail-item">
                        <span className="detail-label">Recipient:</span>
                        <span className="detail-value">
                          {shipment.shippingAddress.recipientName}
                        </span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Address:</span>
                      <span className="detail-value">
                        {shipment.shippingAddress.street}
                        {shipment.shippingAddress.city &&
                          `, ${shipment.shippingAddress.city}`}
                        {shipment.shippingAddress.district &&
                          `, ${shipment.shippingAddress.district}`}
                        {shipment.shippingAddress.country &&
                          `, ${shipment.shippingAddress.country}`}
                      </span>
                    </div>
                    {shipment.shippingAddress.phone && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faPhone} /> Phone:
                        </span>
                        <span className="detail-value">
                          {shipment.shippingAddress.phone}
                        </span>
                      </div>
                    )}
                    {shipment.shippingAddress.country === "Sierra Leone" && (
                      <div className="detail-item">
                        <span className="detail-label">Destination:</span>
                        <span className="detail-value">
                          <span className="destination-tag">
                            🇸🇱 Sierra Leone
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Dates */}
          <div className="detail-section dates-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("dates")}
            >
              <h3>
                <FontAwesomeIcon icon={faCalendar} /> Important Dates
              </h3>
              <span className="toggle-icon">
                {expandedSections.dates ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.dates && (
              <div className="dates-grid">
                <div className="detail-item">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">
                    {formatDate(shipment.createdAt)}
                  </span>
                </div>
                {shipment.shippedAt && (
                  <div className="detail-item">
                    <span className="detail-label">Shipped:</span>
                    <span className="detail-value">
                      {formatDate(shipment.shippedAt)}
                    </span>
                  </div>
                )}
                {shipment.estimatedDelivery && (
                  <div className="detail-item">
                    <span className="detail-label">Est. Delivery:</span>
                    <span className="detail-value">
                      {formatDate(shipment.estimatedDelivery)}
                      {isDelayed && (
                        <span className="delayed-warning"> (Delayed)</span>
                      )}
                    </span>
                  </div>
                )}
                {shipment.deliveredAt && (
                  <div className="detail-item">
                    <span className="detail-label">Delivered:</span>
                    <span className="detail-value">
                      {formatDate(shipment.deliveredAt)}
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Last Updated:</span>
                  <span className="detail-value">
                    {formatDate(shipment.lastUpdated)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          {(shipment.notes || shipment.internalNotes) && (
            <div className="detail-section notes-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("notes")}
              >
                <h3>
                  <FontAwesomeIcon icon={faInfoCircle} /> Notes
                </h3>
                <span className="toggle-icon">
                  {expandedSections.notes ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.notes && (
                <div className="notes-details">
                  {shipment.notes && (
                    <div className="note-item public">
                      <span className="note-label">Public:</span>
                      <p>{shipment.notes}</p>
                    </div>
                  )}
                  {shipment.internalNotes && (
                    <div className="note-item internal">
                      <span className="note-label">Internal:</span>
                      <p>{shipment.internalNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {shipment.customer?.whatsapp && (
            <a
              href={`https://wa.me/${shipment.customer.whatsapp}?text=Hi%20${encodeURIComponent(shipment.customer.name || "")}%2C%20your%20shipment%20${encodeURIComponent(shipment.trackingNumber || "")}%20is%20currently%20${encodeURIComponent(getStatusLabel(shipment.status))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> Contact Customer
            </a>
          )}
        </div>

        <style>{`
          .modal-overlay {
            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
            display: flex; align-items: center; justify-content: center;
            z-index: 1000; padding: 1rem;
          }
          .modal-content {
            background: #fff; border-radius: 12px; max-width: 1000px;
            width: 100%; max-height: 95vh; display: flex;
            flex-direction: column; overflow: hidden;
            box-shadow: 0 20px 60px rgba(0,0,0,0.25);
          }
          .modal-content.large { max-width: 1000px; }
          .modal-header {
            display: flex; justify-content: space-between; align-items: center;
            padding: 1.25rem 1.5rem; border-bottom: 1px solid #e5e5e5;
            flex-shrink: 0; flex-wrap: wrap; gap: 0.5rem;
          }
          .modal-header h2 {
            color: #006b2e; font-size: 1.1rem; margin: 0;
            display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
          }
          .modal-header h2 svg { color: #d9a321; }
          .origin-badge, .supplier-badge {
            font-size: 0.65rem; padding: 0.15rem 0.5rem;
            border-radius: 12px; display: inline-flex; align-items: center; gap: 0.25rem;
          }
          .origin-badge { background: #dcfce7; color: #16a34a; }
          .supplier-badge { background: #fef3c7; color: #d97706; }
          .close-btn {
            background: none; border: none; font-size: 1.25rem;
            cursor: pointer; color: #6b7280; padding: 0.25rem;
            border-radius: 50%; transition: all 0.3s ease;
          }
          .close-btn:hover { background: #f0f0f0; color: #1f1f1f; }

          .modal-body { padding: 1.5rem; overflow-y: auto; flex: 1; }
          .modal-footer {
            padding: 1rem 1.5rem; border-top: 1px solid #e5e5e5;
            display: flex; justify-content: space-between; align-items: center;
            flex-wrap: wrap; gap: 0.5rem; flex-shrink: 0;
          }
          .btn-secondary {
            padding: 0.4rem 1.2rem; background: #f0f0f0;
            color: #1f1f1f; border: 1px solid #e5e5e5;
            border-radius: 6px; cursor: pointer; font-weight: 500;
          }
          .btn-secondary:hover { background: #e5e5e5; }
          .btn-whatsapp {
            padding: 0.4rem 1.2rem; background: #25d366; color: #fff;
            border: none; border-radius: 6px; cursor: pointer;
            font-weight: 600; text-decoration: none;
            display: inline-flex; align-items: center; gap: 0.5rem;
            transition: all 0.3s ease;
          }
          .btn-whatsapp:hover { background: #1da851; transform: translateY(-2px); }

          .tracking-header {
            display: flex; justify-content: space-between;
            align-items: center; flex-wrap: wrap; gap: 0.5rem;
            padding: 0.75rem 1rem; background: #f8f9fa;
            border-radius: 8px; margin-bottom: 1rem;
          }
          .tracking-number { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
          .tracking-label { font-size: 0.7rem; color: #5a5a5a; }
          .tracking-code {
            font-size: 1rem; font-weight: 700; color: #006b2e;
            font-family: monospace;
          }
          .malaysia-tag {
            font-size: 0.6rem; padding: 0.1rem 0.4rem;
            border-radius: 10px; background: #dcfce7; color: #16a34a;
          }
          .tracking-status-badges { display: flex; gap: 0.25rem; flex-wrap: wrap; }
          .badge {
            font-size: 0.6rem; padding: 0.15rem 0.5rem; border-radius: 10px;
            font-weight: 500;
          }
          .badge.bulk { background: #ede9fe; color: #7c3aed; }
          .badge.inventory { background: #dbeafe; color: #2563eb; }
          .badge.delayed { background: #fee2e2; color: #dc2626; }
          .badge.stock-updated { background: #dcfce7; color: #16a34a; }
          .badge.malaysia { background: #fef3c7; color: #d97706; }

          .progress-section { margin: 1rem 0; }
          .progress-bar {
            width: 100%; height: 8px; background: #e5e5e5;
            border-radius: 4px; overflow: hidden;
          }
          .progress-fill { height: 100%; border-radius: 4px; transition: width 0.6s ease; }
          .progress-labels {
            display: flex; justify-content: space-between;
            margin-top: 0.25rem; font-size: 0.6rem; color: #9ca3af;
          }

          .status-section {
            padding: 1rem; border-radius: 8px; margin-bottom: 1.25rem;
          }
          .status-header {
            display: flex; justify-content: space-between;
            align-items: center; flex-wrap: wrap; gap: 0.5rem;
          }
          .status-display {
            display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
          }
          .status-display svg { font-size: 1.1rem; }
          .status-label { font-weight: 600; font-size: 0.95rem; }
          .current-location, .estimated-delivery, .vessel-name {
            font-size: 0.8rem; color: #5a5a5a;
            display: flex; align-items: center; gap: 0.25rem;
          }
          .btn-edit {
            padding: 0.3rem 0.8rem; background: #d9a321; color: #fff;
            border: none; border-radius: 4px; cursor: pointer;
            font-size: 0.75rem; display: flex; align-items: center; gap: 0.25rem;
          }
          .btn-edit:hover { background: #b8860b; }
          .btn-cancel {
            padding: 0.3rem 0.8rem; background: #f0f0f0; color: #1f1f1f;
            border: 1px solid #e5e5e5; border-radius: 4px; cursor: pointer;
            font-size: 0.75rem; display: flex; align-items: center; gap: 0.25rem;
          }
          .btn-cancel:hover { background: #e5e5e5; }

          .status-update-form {
            margin-top: 0.75rem; padding-top: 0.75rem;
            border-top: 1px solid #e5e5e5;
          }
          .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
          .form-group { display: flex; flex-direction: column; gap: 0.25rem; }
          .form-group label { font-weight: 500; font-size: 0.8rem; }
          .form-group input, .form-group select {
            padding: 0.4rem 0.6rem; border: 1px solid #e5e5e5;
            border-radius: 4px; font-size: 0.8rem;
          }
          .form-group input:focus, .form-group select:focus {
            outline: none; border-color: #d9a321;
          }
          .form-actions { margin-top: 0.5rem; }
          .btn-primary {
            padding: 0.4rem 1.2rem; background: #d9a321; color: #fff;
            border: none; border-radius: 4px; cursor: pointer;
            font-weight: 500; display: inline-flex; align-items: center; gap: 0.25rem;
          }
          .btn-primary:hover:not(:disabled) { background: #b8860b; }
          .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

          .detail-section {
            margin-bottom: 1rem; padding-bottom: 1rem;
            border-bottom: 1px solid #f0f0f0;
          }
          .detail-section:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
          .supplier-section { border-left: 4px solid #d9a321; padding-left: 1rem; }
          .bulk-section { border-left: 4px solid #8b5cf6; padding-left: 1rem; }
          .customs-section { border-left: 4px solid #3b82f6; padding-left: 1rem; }
          .address-section { border-left: 4px solid #10b981; padding-left: 1rem; }

          .section-header-clickable {
            display: flex; justify-content: space-between;
            align-items: center; cursor: pointer;
            padding: 0.25rem 0;
          }
          .section-header-clickable:hover { opacity: 0.7; }
          .section-header-clickable h3 {
            color: #006b2e; font-size: 0.95rem; margin: 0;
            display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;
          }
          .section-header-clickable h3 svg { color: #d9a321; }
          .toggle-icon { color: #5a5a5a; font-size: 0.7rem; }

          .detail-grid {
            display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;
            padding-top: 0.5rem;
          }
          .detail-item {
            display: flex; flex-direction: column;
            padding: 0.2rem 0;
          }
          .detail-label { font-size: 0.65rem; color: #9ca3af; }
          .detail-value {
            font-size: 0.85rem; color: #1f1f1f;
            display: flex; align-items: center; gap: 0.25rem; flex-wrap: wrap;
          }

          .supplier-info-card {
            background: linear-gradient(135deg, #006B2E, #014421);
            padding: 0.75rem 1.25rem; border-radius: 12px;
            display: flex; align-items: center; gap: 1rem;
            color: #ffffff; margin-bottom: 1rem;
          }
          .supplier-info-card .supplier-icon {
            font-size: 1.5rem; color: #D9A321;
          }
          .supplier-info-card .supplier-info { flex: 1; }
          .supplier-info-card .supplier-info strong {
            font-size: 0.875rem; font-family: "Montserrat", sans-serif;
          }
          .supplier-info-card .supplier-info span {
            font-size: 0.65rem; opacity: 0.8; display: block;
          }
          .supplier-info-card .supplier-info span svg { margin: 0 0.25rem; }

          .origin-tag, .country-tag { font-size: 0.7rem; }
          .default-tag {
            font-size: 0.55rem; padding: 0.1rem 0.3rem;
            border-radius: 8px; background: #f0f0f0; color: #5a5a5a;
          }
          .origin-flag { font-size: 0.8rem; margin-left: 0.25rem; }

          .route-points {
            display: flex; align-items: center; gap: 1rem;
            padding: 0.5rem 0;
          }
          .route-point {
            flex: 1; padding: 0.5rem 0.75rem;
            background: #f8f9fa; border-radius: 8px;
          }
          .route-point.origin { border-left: 3px solid #d9a321; }
          .route-point.destination { border-left: 3px solid #10b981; }
          .route-label { font-size: 0.6rem; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; }
          .route-value { font-weight: 600; font-size: 0.9rem; color: #1f1f1f; }
          .route-sub { font-size: 0.7rem; color: #5a5a5a; }
          .route-arrow { font-size: 1.5rem; color: #d9a321; }

          .items-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
          .items-table th {
            background: #f8f9fa; padding: 0.3rem 0.5rem;
            text-align: left; font-weight: 600; color: #5a5a5a;
            border-bottom: 1px solid #e5e5e5;
          }
          .items-table td {
            padding: 0.3rem 0.5rem; border-bottom: 1px solid #f0f0f0;
          }
          .items-table tfoot td { font-weight: 600; border-top: 2px solid #e5e5e5; }

          .timeline { position: relative; padding-left: 1.5rem; }
          .timeline::before {
            content: ''; position: absolute; left: 6px; top: 0; bottom: 0;
            width: 2px; background: #e5e5e5;
          }
          .timeline-item {
            position: relative; padding-bottom: 0.75rem;
            display: flex; gap: 0.75rem;
          }
          .timeline-item:last-child { padding-bottom: 0; }
          .timeline-dot {
            position: absolute; left: -1.25rem; top: 2px;
            width: 12px; height: 12px; border-radius: 50%;
            border: 2px solid #fff; flex-shrink: 0;
          }
          .timeline-content { flex: 1; }
          .timeline-status { font-weight: 600; font-size: 0.85rem; }
          .updated-by { font-weight: 400; font-size: 0.65rem; color: #9ca3af; margin-left: 0.25rem; }
          .timeline-location { font-size: 0.75rem; color: #5a5a5a; }
          .timeline-description { font-size: 0.75rem; color: #5a5a5a; }
          .timeline-date { font-size: 0.65rem; color: #9ca3af; }

          .note-item { padding: 0.5rem 0.75rem; border-radius: 6px; margin-bottom: 0.5rem; }
          .note-item:last-child { margin-bottom: 0; }
          .note-item.public { background: #f0f9ff; border-left: 3px solid #3b82f6; }
          .note-item.internal { background: #fef3c7; border-left: 3px solid #d9a321; }
          .note-label { font-weight: 600; font-size: 0.65rem; color: #5a5a5a; }
          .note-item p { margin: 0.25rem 0 0; font-size: 0.8rem; }

          .user-type-badge {
            font-size: 0.7rem; padding: 0.1rem 0.5rem; border-radius: 10px;
          }
          .user-type-badge.farmer { background: #dcfce7; color: #16a34a; }
          .user-type-badge.customer { background: #dbeafe; color: #2563eb; }

          .status-active { color: #16a34a; }
          .status-inactive { color: #d97706; }
          .delayed-warning { color: #dc2626; font-weight: 600; }
          .destination-tag { font-size: 0.75rem; }

          .phone-link { color: #006B2E; text-decoration: none; }
          .phone-link:hover { text-decoration: underline; }
          .whatsapp-link {
            color: #25d366; text-decoration: none;
            display: inline-flex; align-items: center; gap: 0.25rem;
            transition: all 0.3s ease;
          }
          .whatsapp-link:hover { transform: scale(1.05); }

          .route-badge, .bulk-badge, .customs-badge {
            font-size: 0.6rem; padding: 0.1rem 0.4rem;
            border-radius: 10px; background: #f0f0f0; color: #5a5a5a;
          }
          .origin-summary {
            font-size: 0.6rem; padding: 0.1rem 0.4rem;
            border-radius: 10px; background: #dcfce7; color: #16a34a;
          }
          .product-count, .timeline-count {
            font-size: 0.65rem; color: #5a5a5a; font-weight: 400;
          }

          .table-responsive { overflow-x: auto; }

          @media (max-width: 768px) {
            .form-row, .detail-grid { grid-template-columns: 1fr; }
            .modal-content.large { max-width: 100%; margin: 0.5rem; }
            .route-points { flex-direction: column; gap: 0.5rem; }
            .route-arrow { transform: rotate(90deg); }
            .tracking-header { flex-direction: column; align-items: stretch; }
            .status-header { flex-direction: column; align-items: stretch; }
            .status-display { flex-direction: column; align-items: flex-start; }
            .modal-footer { flex-direction: column; align-items: stretch; }
            .btn-whatsapp { justify-content: center; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ShipmentDetailsModal;
